import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes-tracker.service';
import { STORAGE_KEYS } from '../../../shared/constants/storage-keys';
import { UnsavedChangesRequest } from '../../../shared/models/internal/modals/unsaved-changes-request';

@Directive()
export class UnsavedChangesBase implements OnInit, OnDestroy {
  protected unsavedChangesService = inject(UnsavedChangesTrackerService);
  protected key?: string = STORAGE_KEYS.NONE;

  constructor(key: string) {
    this.key = key;
  }

  public ngOnInit(): void {
    if (this.key === STORAGE_KEYS.NONE) {
      return;
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  public ngOnDestroy(): void {
    if (this.key === STORAGE_KEYS.NONE) {
      return;
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.unsavedChangesService.clearUnsavedChangesFromSessionStorage(this.key ?? '');
  }

  protected clearUnsavedData(): void {
    this.unsavedChangesService.clearUnsavedChangesFromSessionStorage(this.key ?? '');
  }

  /**
   * Checks for unsaved changes and opens the modal if there are any.
   * @param executeOnLeave Function to execute if the user decides to leave despite unsaved changes.
   * @param executeOnStay Function to execute if the user decides to stay and not lose changes.
   * @param title Title for the unsaved changes modal.
   * @returns True if there were unsaved changes and the modal was opened, false otherwise.
   */
  protected areThereUnsavedChanges<T>(title: string, executeOnLeave?: () => void, executeOnStay?: () => void): boolean {
    const unsavedItem = this.unsavedChangesService.getUnsavedChangesFromSessionStorage<T>(this.key ?? '');
    if (unsavedItem || this.unsavedChangesService.anyUnsavedChanges()) {
      const requestModal = {
        title: title,
        onLeave: executeOnLeave,
        onStay: executeOnStay,
      } as UnsavedChangesRequest;
      this.unsavedChangesService.showUnsavedChanges(requestModal);
      return true;
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    this.clearUnsavedData();
  };
}
